import { createContext, useContext, useState, useEffect } from 'react';
import {
  getCart,
  addToCart as addToCartAPI,
  updateItem as updateItemAPI,
  removeItem as removeItemAPI,
  clearCart as clearCartAPI,
  mergeCartAPI,
} from '../services/api';

const CartContext = createContext();

function isLoggedIn() {
  return !!localStorage.getItem('access_token');
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('cart')) || [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);

  /* ─── Load cart from API if logged in ─── */
  useEffect(() => {
    if (isLoggedIn()) {
      setLoading(true);
      getCart()
        .then((res) => {
          setCart(res.data.items || []);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, []);

  /* ─── Persist guest cart to localStorage ─── */
  useEffect(() => {
    if (!isLoggedIn()) {
      localStorage.setItem('cart', JSON.stringify(cart));
    }
  }, [cart]);

  /* ─── Add to cart (hybrid) ─── */
  const addToCart = async (product, quantity = 1, size = '') => {
    if (isLoggedIn()) {
      try {
        const res = await addToCartAPI({
          product_id: product.id,
          quantity,
          size,
        });
        setCart(res.data.items || []);
      } catch (e) {
        console.error('Add to cart failed', e);
      }
      return;
    }

    /* Guest — localStorage */
    setCart((prev) => {
      const existing = prev.find(
        (i) => i.id === product.id && (i.size || '') === size
      );
      if (existing) {
        return prev.map((i) =>
          i.id === product.id && (i.size || '') === size
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { ...product, quantity, size }];
    });
  };

  /* ─── Remove from cart ─── */
  const removeFromCart = async (productId) => {
    if (isLoggedIn()) {
      // item id is what API needs — find item
      const item = cart.find((i) => i.product_id === productId || i.id === productId);
      if (!item) return;
      try {
        const res = await removeItemAPI(item.id);
        setCart(res.data.items || []);
      } catch (e) {}
      return;
    }
    setCart((prev) => prev.filter((i) => i.id !== productId));
  };

  /* ─── Update quantity ─── */
  const updateQuantity = async (productId, quantity) => {
    if (quantity < 1) return removeFromCart(productId);

    if (isLoggedIn()) {
      const item = cart.find(
        (i) => i.product_id === productId || i.id === productId
      );
      if (!item) return;
      try {
        const res = await updateItemAPI(item.id, { quantity });
        setCart(res.data.items || []);
      } catch (e) {}
      return;
    }
    setCart((prev) =>
      prev.map((i) => (i.id === productId ? { ...i, quantity } : i))
    );
  };

  /* ─── Clear cart ─── */
  const clearCart = async () => {
    if (isLoggedIn()) {
      try {
        await clearCartAPI();
        setCart([]);
      } catch (e) {}
      return;
    }
    setCart([]);
  };

  /* ─── Merge localStorage cart into DB (called after login) ─── */
  const mergeLocalCart = async () => {
    if (!isLoggedIn()) return;
    const local = JSON.parse(localStorage.getItem('cart') || '[]');
    if (local.length === 0) return;

    const items = local.map((i) => ({
      product_id: i.id,
      quantity: i.quantity,
      size: i.size || '',
    }));

    try {
      const res = await mergeCartAPI(items);
      setCart(res.data.items || []);
      localStorage.removeItem('cart');
    } catch (e) {
      console.error('Cart merge failed', e);
    }
  };

  /* ─── Totals ─── */
  const totalItems = cart.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = cart.reduce(
    (sum, i) => sum + parseFloat(i.price || 0) * i.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        mergeLocalCart,
        totalItems,
        totalPrice,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);